import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { Sentry } from "@/lib/sentry";
import { REALTIME_TABLES } from "./realtime-tables";

/**
 * Mountes én gang etter at brukeren er innlogget. Starter Supabase
 * realtime-subscriptions på alle tabeller i REALTIME_TABLES. Hver event
 * ekstraherer context og kaller invalidateFor — fan-out til riktige
 * queryKeys håndteres av CACHE_REGISTRY.
 *
 * Bruk:
 *   const { user } = useAuth();
 *   useRealtimeListeners(!!user);
 */
export function useRealtimeListeners(enabled: boolean): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const channels = REALTIME_TABLES.map((config) => {
      const channel = supabase
        .channel(config.channelId)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: config.table },
          (payload) => {
            const context = config.extractContext(payload);
            void invalidateFor(qc, config.table, context);
          },
        )
        .subscribe((status, err) => {
          if (status === "CHANNEL_ERROR" && err) {
            Sentry.captureException(err, {
              tags: { realtime_channel: config.channelId },
            });
          }
        });
      return channel;
    });

    return () => {
      for (const channel of channels) {
        supabase.removeChannel(channel);
      }
    };
  }, [enabled, qc]);
}
